import { FC, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { deleteResource, listResource } from "data/adminApi";
import ConfirmDialog from "components/Admin/ConfirmDialog";
import {
  MagnifyingGlassIcon,
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";

interface ProductRow {
  id: number;
  SKU: string;
  name: string;
  status: string;
  featured: boolean;
  subcategory_name?: string;
}

const AdminProducts: FC = () => {
  const [products, setProducts] = useState<ProductRow[]>([]);
  const [query, setQuery] = useState("");
  const [deleteSku, setDeleteSku] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const load = async (search = "") => {
    const data = await listResource<ProductRow>("/products/", {
      search: search || undefined,
    });
    setProducts(data);
  };

  useEffect(() => {
    load().catch(() => setMessage("Failed to load products."));
  }, []);

  const confirmDelete = async () => {
    if (!deleteSku) return;
    setLoading(true);
    try {
      await deleteResource(`/products/${encodeURIComponent(deleteSku)}/`);
      await load(query);
      setMessage("Product deleted.");
    } catch {
      setMessage("Could not delete product.");
    } finally {
      setLoading(false);
      setDeleteSku(null);
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">
            Products
          </h1>
          <p className="mt-1 text-sm text-neutral-500">
            Create and edit product catalogue items.
          </p>
        </div>
        <Link
          to="/admin/products/new"
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bcr-btn-primary text-sm font-semibold"
        >
          <PlusIcon className="w-4 h-4" />
          Add product
        </Link>
      </div>

      {message && (
        <div className="mt-4 rounded-xl bg-primary-50 text-primary-800 text-sm px-4 py-3">
          {message}
        </div>
      )}

      <div className="mt-6 relative max-w-md">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") load(query);
          }}
          placeholder="Search SKU or name…"
          className="w-full h-11 pl-10 pr-4 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900"
        />
      </div>

      <div className="mt-6 rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-neutral-50 dark:bg-neutral-800/50 text-left">
            <tr>
              <th className="px-4 py-3 font-semibold">SKU</th>
              <th className="px-4 py-3 font-semibold">Name</th>
              <th className="px-4 py-3 font-semibold">Category</th>
              <th className="px-4 py-3 font-semibold">Status</th>
              <th className="px-4 py-3 font-semibold">Featured</th>
              <th className="px-4 py-3 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800">
            {products.map((p) => (
              <tr key={p.id}>
                <td className="px-4 py-3 font-mono text-xs">{p.SKU}</td>
                <td className="px-4 py-3 font-medium">{p.name}</td>
                <td className="px-4 py-3 text-neutral-500">
                  {p.subcategory_name}
                </td>
                <td className="px-4 py-3">{p.status}</td>
                <td className="px-4 py-3">{p.featured ? "Yes" : "No"}</td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-2">
                    <Link
                      to={`/admin/products/${encodeURIComponent(p.SKU)}/edit`}
                      className="p-2 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800"
                    >
                      <PencilSquareIcon className="w-4 h-4" />
                    </Link>
                    <button
                      type="button"
                      onClick={() => setDeleteSku(p.SKU)}
                      className="p-2 rounded-lg hover:bg-red-50 text-red-600"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {products.length === 0 && (
              <tr>
                <td
                  colSpan={6}
                  className="px-4 py-10 text-center text-neutral-500"
                >
                  No products found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <ConfirmDialog
        open={!!deleteSku}
        title="Delete product?"
        message={`Delete product ${deleteSku}? This cannot be undone.`}
        loading={loading}
        onClose={() => setDeleteSku(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
};

export default AdminProducts;
